import React, { useEffect, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import {
  createStaffAccount,
  deleteStaffAccount,
  newId,
  OFFLINE_ERROR,
  setStaffPassword,
  useAppStore,
} from '@restorant/core';
import type { Restaurant, StaffMember, StaffRole } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { EmptyNote } from '../components/EmptyNote';
import { SectionTitle } from '../components/SectionTitle';
import { Sheet, SheetButton, Toggle } from '../components/Sheet';
import { T } from '../components/Text';
import { TextField } from '../components/TextField';

/* ---------------- restaurant editor ---------------- */

function RestaurantEditor({
  restaurant,
  visible,
  onClose,
}: {
  restaurant: Restaurant | null;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const upsert = useAppStore((s) => s.upsertRestaurant);
  const showToast = useAppStore((s) => s.showToast);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [gstin, setGstin] = useState('');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(restaurant?.name ?? '');
    setAddress(restaurant?.address ?? '');
    setPhone(restaurant?.phone ?? '');
    setGstin(restaurant?.gstin ?? '');
    setActive(restaurant?.active ?? true);
    setError(null);
  }, [restaurant, visible]);

  const save = () => {
    if (!name.trim()) return setError(t('name_required'));
    upsert({
      // A uuid, because the database generates uuids for rows created there and
      // the two must be interchangeable.
      id: restaurant?.id ?? newId(),
      name: name.trim(),
      address: address.trim(),
      phone: phone.trim(),
      gstin: gstin.trim().toUpperCase(),
      active,
    });
    showToast(t('saved_toast'));
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={restaurant ? t('edit_restaurant') : t('add_restaurant')}
      onClose={onClose}
      footer={<SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />}
    >
      <TextField testID="rest-name" label={t('restaurant_name')} value={name} onChangeText={setName} />
      <TextField testID="rest-address" label={t('address')} value={address} onChangeText={setAddress} />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="rest-phone"
          label={t('customer_phone')}
          icon="phone"
          keyboardType="phone-pad"
          value={phone}
          onChangeText={setPhone}
        />
        <TextField testID="rest-gstin" label={t('gstin')} value={gstin} onChangeText={setGstin} />
      </View>
      <Toggle label={t('active_label')} value={active} onChange={setActive} />
      {error ? <T size={12} color={colors.spice}>{error}</T> : null}
    </Sheet>
  );
}

/* ---------------- staff editor ---------------- */

function StaffEditor({
  member,
  visible,
  allowOwnerRole,
  onClose,
}: {
  member: StaffMember | null;
  visible: boolean;
  /**
   * Whether 'owner' may be picked. False for a restaurant owner managing their
   * own workers: the database refuses that write anyway, and offering a choice
   * that always fails is worse than not offering it.
   */
  allowOwnerRole: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const staff = useAppStore((s) => s.staff);
  const restaurantId = useAppStore((s) => s.restaurantId);
  const userEmail = useAppStore((s) => s.userEmail);
  const upsert = useAppStore((s) => s.upsertStaff);
  const remove = useAppStore((s) => s.removeStaff);
  const showToast = useAppStore((s) => s.showToast);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<StaffRole>('worker');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setEmail(member?.email ?? '');
    // Never pre-filled, even when editing: a password an owner set is not
    // readable, only replaceable.
    setPassword('');
    setRole(member?.role ?? 'worker');
    setActive(member?.active ?? true);
    setError(null);
    setBusy(false);
    setConfirmDelete(false);
  }, [member, visible]);

  // Removing or demoting yourself would lock the restaurant out of its own
  // Admin screen, so your own row is read-only here.
  const isSelf =
    !!member?.email && !!userEmail && member.email.toLowerCase() === userEmail.toLowerCase();

  /**
   * Adding somebody creates their login, so it has to reach the server; role
   * and availability are ordinary edits and still go through the outbox. The
   * two halves are deliberately not merged — a shop with a flaky connection
   * should still be able to demote a worker mid-service.
   */
  const save = async () => {
    if (busy) return;
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) return setError(t('email_required'));
    if (staff.some((m) => m.id !== member?.id && m.email.toLowerCase() === value)) {
      return setError(t('email_taken'));
    }
    if (!restaurantId) return;
    if ((!member || password) && password.length < 6) {
      return setError(t('password_too_short'));
    }

    setError(null);
    setBusy(true);
    try {
      let reused = false;
      if (!member) {
        const created = await createStaffAccount({
          restaurantId,
          email: value,
          password,
          role,
          active,
        });
        upsert(created.member);
        reused = created.reusedExistingLogin;
      } else {
        if (password) await setStaffPassword(member.id, password);
        upsert({ ...member, email: value, role, active });
      }
      setBusy(false);
      // Say so when the password was not the one just typed, or the owner will
      // read it out and watch it be refused.
      if (reused) {
        setError(t('staff_login_exists'));
        return;
      }
      showToast(password && member ? t('password_set_toast') : t('saved_toast'));
    } catch (e) {
      setBusy(false);
      return setError(describe(e));
    }
    onClose();
  };

  // Taking somebody off the roster has to take their login with it, or a
  // dismissed worker still has a password that works.
  const destroy = async () => {
    if (!member || busy) return;
    if (!confirmDelete) return setConfirmDelete(true);
    setBusy(true);
    try {
      await deleteStaffAccount(member.id);
    } catch (e) {
      setBusy(false);
      return setError(describe(e));
    }
    remove(member.id);
    setBusy(false);
    showToast(t('deleted_toast'));
    onClose();
  };

  // A failed fetch means the shop's wifi, not anything the owner did wrong.
  const describe = (e: unknown) =>
    e instanceof Error && e.name === OFFLINE_ERROR
      ? t('staff_needs_online')
      : e instanceof Error
        ? e.message
        : String(e);

  const roles: { id: StaffRole; label: string; hint: string }[] = [
    ...(allowOwnerRole
      ? [{ id: 'owner' as StaffRole, label: t('role_owner'), hint: t('role_owner_hint') }]
      : []),
    { id: 'worker', label: t('role_worker'), hint: t('role_worker_hint') },
  ];

  return (
    <Sheet
      visible={visible}
      title={member ? t('edit_staff') : t('add_staff')}
      onClose={onClose}
      footer={
        <>
          {member && !isSelf ? (
            <SheetButton
              label={confirmDelete ? t('remove_staff_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={() => void destroy()}
            />
          ) : null}
          {isSelf ? null : (
            <SheetButton
              label={busy ? t('signing_in') : t('save')}
              icon="check"
              tone="primary"
              onPress={() => void save()}
            />
          )}
        </>
      }
    >
      <TextField
        testID="staff-email"
        label={t('staff_email')}
        value={email}
        onChangeText={setEmail}
        editable={!member}
        placeholder="name@example.com"
      />

      {/* The owner sets the first password and says it out loud. Nothing is
          mailed, so this field is the only way a login ever comes into being —
          and, for somebody who has forgotten theirs, the only way back in. */}
      <TextField
        testID="staff-password"
        label={member ? t('staff_password_new') : t('staff_password')}
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        placeholder={member ? '••••••' : ''}
      />
      <T size={11.5} color={colors.textFaint} style={{ lineHeight: 17 }}>
        {member ? t('staff_password_reset_hint') : t('staff_password_hint')}
      </T>

      <View style={{ gap: 8 }}>
        <T size={11.5} weight={600} color={colors.textFaint}>
          {t('staff_role')}
        </T>
        {roles.map((r) => {
          const on = role === r.id;
          return (
            <Pressable
              key={r.id}
              onPress={() => !isSelf && setRole(r.id)}
              testID={`role-${r.id}`}
              style={{
                gap: 3,
                padding: 13,
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: on ? colors.gold : colors.line,
                backgroundColor: on ? colors.surface2 : 'transparent',
                opacity: isSelf ? 0.6 : 1,
              }}
            >
              <T size={13.5} weight={700} color={on ? colors.gold : colors.text}>
                {r.label}
              </T>
              <T size={11.5} color={colors.textFaint}>
                {r.hint}
              </T>
            </Pressable>
          );
        })}
      </View>

      {isSelf ? null : <Toggle label={t('active_label')} value={active} onChange={setActive} />}
      {error ? <T size={12} color={colors.spice}>{error}</T> : null}
    </Sheet>
  );
}

/* ---------------- screen ---------------- */

function Row({
  title,
  detail,
  icon,
  accent,
  onPress,
}: {
  title: string;
  detail: string;
  icon: 'store' | 'user';
  accent?: boolean;
  onPress: () => void;
}) {
  const colors = useColors();
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : accent ? colors.goldDim : colors.line,
        backgroundColor: colors.surface,
        marginBottom: 8,
      })}
    >
      <Icon name={icon} size={17} color={accent ? colors.gold : colors.textFaint} />
      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={13.5} weight={700} numberOfLines={1}>
          {title}
        </T>
        <T size={11.5} color={colors.textFaint} numberOfLines={1}>
          {detail}
        </T>
      </View>
      <Icon name="edit" size={15} color={colors.textFaint} />
    </Pressable>
  );
}

/**
 * One component, two jobs, decided by `mode`.
 *
 *   'admin'  the administrator's screen: every restaurant, and the owner of
 *            each. Appointing an owner is the one thing only they can do.
 *   'staff'  a restaurant owner's screen: the workers of the shop they run,
 *            and nothing else — no restaurant list, no owners.
 *
 * They share this file because the roster row, the editor sheet and the
 * password handling are identical; splitting them would have meant two copies
 * of the part that is easy to get subtly wrong. What differs is only which
 * sections render and which roles may be chosen.
 *
 * Web only in both modes. It is deliberately not on the phone: hiring somebody
 * is an office job, and putting it on the counter tablet is how a worker ends
 * up tapping it mid-service.
 */
export function AdminScreen({
  contentPadding,
  mode = 'admin',
}: {
  contentPadding: number;
  mode?: 'admin' | 'staff';
}) {
  const colors = useColors();
  const t = useT();
  const role = useAppStore((s) => s.role);
  const isPlatformAdmin = useAppStore((s) => s.isPlatformAdmin);
  const restaurants = useAppStore((s) => s.restaurants);
  const restaurantId = useAppStore((s) => s.restaurantId);
  const staff = useAppStore((s) => s.staff);
  const selectRestaurant = useAppStore((s) => s.selectRestaurant);

  const adminMode = mode === 'admin';

  // An owner manages workers. The other owners of their restaurant are not
  // theirs to edit — the RLS policy refuses any write to a row whose role is
  // not 'worker' — so they are left off the list entirely rather than shown as
  // rows that only fail once you press Save.
  const roster = adminMode ? staff : staff.filter((m) => m.role === 'worker');

  const [shopEditor, setShopEditor] = useState<{ open: boolean; item: Restaurant | null }>({
    open: false,
    item: null,
  });
  const [staffEditor, setStaffEditor] = useState<{ open: boolean; item: StaffMember | null }>({
    open: false,
    item: null,
  });

  const notice =
    Platform.OS !== 'web'
      ? t(adminMode ? 'admin_web_only' : 'staff_web_only')
      : adminMode
        ? isPlatformAdmin
          ? null
          : t('admin_admin_only')
        : role === 'owner'
          ? null
          : t('staff_owner_only');

  if (notice) {
    return (
      <Screen contentPadding={contentPadding} paddingBottom={contentPadding}>
        <EmptyNote>{notice}</EmptyNote>
      </Screen>
    );
  }

  return (
    <Screen contentPadding={contentPadding}>
      {/* An owner runs the shop they were given. The list of every restaurant
          in the system is the administrator's business, not theirs. */}
      {adminMode ? (
        <>
      <SectionTitle title={t('restaurants')} icon="store" first />
      {restaurants.map((r) => (
        <Row
          key={r.id}
          icon="store"
          accent={r.id === restaurantId}
          title={r.name}
          detail={
            [r.id === restaurantId ? t('switch_restaurant') : null, r.address || r.gstin]
              .filter(Boolean)
              .join(' · ') || '—'
          }
          onPress={() => {
            // Tapping the shop you are already in edits it; tapping another
            // switches to it, which is the more likely intent.
            if (r.id === restaurantId) setShopEditor({ open: true, item: r });
            else selectRestaurant(r.id);
          }}
        />
      ))}

      <Pressable
        onPress={() => setShopEditor({ open: true, item: null })}
        testID="add-restaurant"
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          paddingVertical: 14,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="plus" size={16} color={colors.textDim} />
        <T size={13} weight={700} color={colors.textDim}>
          {t('add_restaurant')}
        </T>
      </Pressable>
        </>
      ) : null}

      <SectionTitle
        title={adminMode ? t('staff') : t('workers')}
        icon="user"
        first={!adminMode}
      />
      {roster.length ? (
        roster.map((m) => (
          <Row
            key={m.id}
            icon="user"
            title={m.email}
            detail={[
              m.role === 'owner' ? t('role_owner') : t('role_worker'),
              m.userId ? null : t('invite_pending'),
              m.active ? null : t('hidden_label'),
            ]
              .filter(Boolean)
              .join(' · ')}
            onPress={() => setStaffEditor({ open: true, item: m })}
          />
        ))
      ) : (
        <EmptyNote>{t(adminMode ? 'no_open_orders' : 'no_workers')}</EmptyNote>
      )}

      <Pressable
        onPress={() => setStaffEditor({ open: true, item: null })}
        testID="add-staff"
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          paddingVertical: 14,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="plus" size={16} color={colors.textDim} />
        <T size={13} weight={700} color={colors.textDim}>
          {t(adminMode ? 'add_staff' : 'add_worker')}
        </T>
      </Pressable>

      {adminMode ? null : (
        <T size={11.5} color={colors.textFaint} style={{ paddingTop: 10 }}>
          {t('owner_appointed_by_admin')}
        </T>
      )}

      <RestaurantEditor
        visible={shopEditor.open}
        restaurant={shopEditor.item}
        onClose={() => setShopEditor({ open: false, item: null })}
      />
      <StaffEditor
        visible={staffEditor.open}
        member={staffEditor.item}
        allowOwnerRole={adminMode}
        onClose={() => setStaffEditor({ open: false, item: null })}
      />
    </Screen>
  );
}
